import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { findUserByEmail, deleteUserFromFirestore } from '../../services/firestoreService';

const UserManagement = () => {
  const { hasRole } = useAuth();
  const [email, setEmail] = useState('');
  const [foundUser, setFoundUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const isAdmin = hasRole('admin');

  if (!isAdmin) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          You don't have permission to access user management.
        </div>
      </div>
    );
  }

  const handleSearchUser = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setMessage('Please enter an email address');
      return;
    }

    setLoading(true);
    setMessage('');
    setFoundUser(null);

    try {
      const user = await findUserByEmail(email.trim());
      if (user) {
        setFoundUser(user);
        setMessage(`User found: ${user.displayName || user.firstName + ' ' + user.lastName}`);
      } else {
        setMessage('No user found with that email address');
      }
    } catch (error) {
      console.error('Error searching for user:', error);
      setMessage('Error searching for user: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!foundUser) return;

    const confirmDelete = window.confirm(
      `Are you sure you want to delete user ${foundUser.email}? This action cannot be undone.`
    );

    if (!confirmDelete) return;

    setLoading(true);
    setMessage('');

    try {
      await deleteUserFromFirestore(foundUser.id);
      setMessage(`✅ User ${foundUser.email} has been deleted from Firestore. You may also need to delete them from Firebase Authentication manually.`);
      setFoundUser(null);
      setEmail('');
    } catch (error) {
      console.error('Error deleting user:', error);
      setMessage('❌ Error deleting user: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">User Management</h1>
        
        {/* Search User Form */}
        <form onSubmit={handleSearchUser} className="mb-6">
          <div className="flex gap-4">
            <div className="flex-1">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Search User by Email
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter email address"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                disabled={loading}
              />
            </div>
            <div className="flex items-end">
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-apn-blue text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {loading ? 'Searching...' : 'Search'}
              </button>
            </div>
          </div>
        </form>

        {/* Message Display */}
        {message && (
          <div className={`p-4 rounded-md mb-6 ${
            message.includes('✅') ? 'bg-green-100 text-green-700' : 
            message.includes('❌') ? 'bg-red-100 text-red-700' : 
            'bg-blue-100 text-blue-700'
          }`}>
            {message}
          </div>
        )}

        {/* User Details */}
        {foundUser && (
          <div className="bg-gray-50 p-6 rounded-lg mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">User Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p><strong>Email:</strong> {foundUser.email}</p>
                <p><strong>Name:</strong> {foundUser.displayName || `${foundUser.firstName} ${foundUser.lastName}`}</p>
                <p><strong>Status:</strong> {foundUser.status || 'pending'}</p>
                <p><strong>Role:</strong> {foundUser.role || 'member'}</p>
              </div>
              <div>
                <p><strong>Company:</strong> {foundUser.company || 'N/A'}</p>
                <p><strong>Job Title:</strong> {foundUser.jobTitle || 'N/A'}</p>
                <p><strong>Created:</strong> {foundUser.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}</p>
                <p><strong>User ID:</strong> {foundUser.id}</p>
              </div>
            </div>
            
            {/* Delete Button */}
            <div className="mt-6 pt-4 border-t border-gray-200">
              <button
                onClick={handleDeleteUser}
                disabled={loading}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {loading ? 'Deleting...' : 'Delete User from Firestore'}
              </button>
              <p className="text-sm text-gray-500 mt-2">
                ⚠️ This will delete the user from Firestore database. You may also need to delete them from Firebase Authentication manually.
              </p>
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="bg-yellow-50 p-4 rounded-lg">
          <h4 className="font-semibold text-yellow-800 mb-2">Instructions:</h4>
          <ol className="list-decimal list-inside text-sm text-yellow-700 space-y-1">
            <li>Search for a user by their email address</li>
            <li>Review the user details carefully</li>
            <li>Click "Delete User from Firestore" to remove them from the database</li>
            <li>Manually delete the user from Firebase Authentication Console if needed</li>
            <li>The user will then be able to create a new account with the same email</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;
